import { supabase } from '../lib/supabase';

/**
 * Team Service - Handles all team-related operations with Supabase
 * UPDATED: Queries user_profiles directly instead of non-existent team_members table
 */

/**
 * Get all teams with their members from user_profiles
 * @returns {Promise<Array>} Array of teams with member details
 */
export const getAllTeams = async () => {
  try {
    const { data, error } = await supabase
      ?.from('teams')
      ?.select(`
        *,
        user_profiles!user_profiles_team_id_fkey (
          id,
          full_name,
          email,
          role,
          organization,
          skills
        )
      `)
      ?.order('team_number', { ascending: true });

    if (error) throw error;

    // Transform data to match expected format
    return data?.map(team => ({
      id: team?.id,
      teamNumber: team?.team_number,
      teamName: team?.team_name,
      description: team?.description,
      problemStatement: team?.problem_statement,
      pointOfContact: team?.point_of_contact,
      mentor: team?.manager,
      mentorEmail: team?.manager_email,
      cityOfficial: team?.city_official,
      city: team?.city,
      category: team?.category,
      finalDueDate: team?.final_due_date,
      status: team?.status,
      workSummary: team?.work_summary,
      stageRequirements: team?.stage_requirements,
      currentMembers: team?.current_members,
      maxMembers: team?.max_members,
      createdAt: team?.created_at,
      members: team?.user_profiles?.map(profile => ({
        id: profile?.id,
        userId: profile?.id,
        fullName: profile?.full_name,
        email: profile?.email,
        role: profile?.role,
        organization: profile?.organization,
        skills: profile?.skills,
        isLeader: false // Can be determined by business logic if needed
      })) || []
    })) || [];
  } catch (error) {
    console.error('Error fetching teams:', error);
    throw new Error(error?.message || 'Failed to fetch teams');
  }
};

/**
 * Get a single team by ID with full details from user_profiles
 * @param {string} teamId - Team UUID
 * @returns {Promise<Object>} Team object with member details
 */
export const getTeamById = async (teamId) => {
  try {
    const { data, error } = await supabase
      ?.from('teams')
      ?.select(`
        *,
        user_profiles!user_profiles_team_id_fkey (
          id,
          full_name,
          email,
          organization,
          role,
          skills,
          bio
        )
      `)
      ?.eq('id', teamId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      teamNumber: data?.team_number,
      teamName: data?.team_name,
      description: data?.description,
      problemStatement: data?.problem_statement,
      pointOfContact: data?.point_of_contact,
      mentor: data?.manager,
      mentorEmail: data?.manager_email,
      cityOfficial: data?.city_official,
      city: data?.city,
      category: data?.category,
      finalDueDate: data?.final_due_date,
      status: data?.status,
      workSummary: data?.work_summary,
      stageRequirements: data?.stage_requirements,
      currentMembers: data?.current_members,
      maxMembers: data?.max_members,
      createdAt: data?.created_at,
      members: data?.user_profiles?.map(profile => ({
        id: profile?.id,
        userId: profile?.id,
        fullName: profile?.full_name,
        email: profile?.email,
        organization: profile?.organization,
        role: profile?.role,
        skills: profile?.skills,
        bio: profile?.bio,
        isLeader: false // Can be determined by business logic if needed
      })) || []
    };
  } catch (error) {
    console.error('Error fetching team:', error);
    throw new Error(error?.message || 'Failed to fetch team');
  }
};

/**
 * Get teams filtered by city official - only teams assigned to approved problem statements
 * @param {string} cityOfficialId - City official user ID
 * @returns {Promise<Array>} Array of unique teams assigned to approved problem statements
 */
export const getTeamsByCityOfficial = async (cityOfficialId) => {
  try {
    // Step 1: Get all approved problem statements submitted by this city official
    const { data: approvedProblems, error: problemsError } = await supabase
      ?.from('problem_statements')
      ?.select('assigned_team_id')
      ?.eq('submitted_by', cityOfficialId)
      ?.eq('status', 'approved')
      ?.not('assigned_team_id', 'is', null);

    if (problemsError) throw problemsError;

    // If no approved problems with assigned teams, return empty array
    if (!approvedProblems || approvedProblems?.length === 0) {
      return [];
    }

    // Step 2: Extract unique team IDs
    const uniqueTeamIds = [...new Set(approvedProblems?.map(p => p?.assigned_team_id)?.filter(Boolean))];

    if (uniqueTeamIds?.length === 0) {
      return [];
    }

    // Step 3: Fetch full team details for these unique team IDs
    const { data, error } = await supabase
      ?.from('teams')
      ?.select(`
        *,
        members:user_profiles!user_profiles_team_id_fkey(
          id,
          full_name,
          email,
          role,
          team_role,
          profile_image_url
        )
      `)
      ?.in('id', uniqueTeamIds)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching teams by city official:', error);
    throw error;
  }
};

/**
 * Create a new team
 * @param {Object} teamData - Team creation data
 * @returns {Promise<Object>} Created team object
 */
export const createTeam = async (teamData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('teams')?.insert([{
        team_number: teamData?.teamNumber,
        team_name: teamData?.teamName,
        description: teamData?.description,
        problem_statement: teamData?.problemStatement,
        point_of_contact: teamData?.pointOfContact,
        manager: teamData?.mentor,
        manager_email: teamData?.mentorEmail,
        city: teamData?.city,
        category: teamData?.category,
        final_due_date: teamData?.projectDeadline,
        created_by: user?.id,
        status: 'forming'
      }])?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      teamNumber: data?.team_number,
      teamName: data?.team_name,
      description: data?.description,
      problemStatement: data?.problem_statement,
      pointOfContact: data?.point_of_contact,
      mentor: data?.manager,
      mentorEmail: data?.manager_email,
      city: data?.city,
      category: data?.category,
      finalDueDate: data?.final_due_date,
      status: data?.status,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error creating team:', error);
    throw new Error(error?.message || 'Failed to create team');
  }
};

/**
 * Update a team
 * @param {string} teamId - Team UUID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Updated team object
 */
export const updateTeam = async (teamId, updateData) => {
  try {
    const updates = {};
    
    if (updateData?.teamName) updates.team_name = updateData?.teamName;
    if (updateData?.teamNumber !== undefined) updates.team_number = updateData?.teamNumber;
    if (updateData?.description) updates.description = updateData?.description;
    if (updateData?.problemStatement) updates.problem_statement = updateData?.problemStatement;
    if (updateData?.pointOfContact) updates.point_of_contact = updateData?.pointOfContact;
    if (updateData?.mentor) updates.manager = updateData?.mentor;
    if (updateData?.mentorEmail) updates.manager_email = updateData?.mentorEmail;
    if (updateData?.cityOfficial) updates.city_official = updateData?.cityOfficial;
    if (updateData?.city) updates.city = updateData?.city;
    if (updateData?.category) updates.category = updateData?.category;
    if (updateData?.projectDeadline !== undefined) updates.final_due_date = updateData?.projectDeadline;
    if (updateData?.status) updates.status = updateData?.status;
    if (updateData?.workSummary) updates.work_summary = updateData?.workSummary;

    const { data, error } = await supabase
      ?.from('teams')
      ?.update(updates)
      ?.eq('id', teamId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      teamNumber: data?.team_number,
      teamName: data?.team_name,
      description: data?.description,
      problemStatement: data?.problem_statement,
      pointOfContact: data?.point_of_contact,
      mentor: data?.manager,
      mentorEmail: data?.manager_email,
      cityOfficial: data?.city_official,
      city: data?.city,
      category: data?.category,
      finalDueDate: data?.final_due_date,
      status: data?.status,
      workSummary: data?.work_summary,
      updatedAt: data?.updated_at
    };
  } catch (error) {
    console.error('Error updating team:', error);
    throw new Error(error?.message || 'Failed to update team');
  }
};

/**
 * Delete a team
 * @param {string} teamId - Team UUID
 * @returns {Promise<void>}
 */
export const deleteTeam = async (teamId) => {
  try {
    const { error } = await supabase?.from('teams')?.delete()?.eq('id', teamId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting team:', error);
    throw new Error(error?.message || 'Failed to delete team');
  }
};

/**
 * Update stage upload progress
 * @param {string} teamId - Team UUID
 * @param {string} stageName - Stage name (snake_case)
 * @param {number} completedCount - Number of completed uploads
 * @returns {Promise<Object>} Updated team object
 */
export const updateStageProgress = async (teamId, stageName, completedCount) => {
  try {
    // Get current stage requirements
    const { data: team, error: fetchError } = await supabase?.from('teams')?.select('stage_requirements')?.eq('id', teamId)?.single();

    if (fetchError) throw fetchError;

    // Update the specific stage's completed uploads
    const updatedRequirements = {
      ...team?.stage_requirements,
      [stageName]: {
        ...team?.stage_requirements?.[stageName],
        completed_uploads: completedCount
      }
    };

    const { data, error } = await supabase?.from('teams')?.update({ stage_requirements: updatedRequirements })?.eq('id', teamId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      stageRequirements: data?.stage_requirements
    };
  } catch (error) {
    console.error('Error updating stage progress:', error);
    throw new Error(error?.message || 'Failed to update stage progress');
  }
};